import { useEffect, useState } from 'react';
import { BottomModal } from '../../../components';
import {
  dispatch,
  getOrderPricelist,
  getWorkspaceId,
  OrderPricelist,
  Pricing,
  removeSearchKey,
  useAppSelector,
  useGetWorkspacePricelistMutation,
} from '@zonofi/zono-money-store';
import { SelectPriceListDrawerContent } from './select-price-list-drawer-content';
import { isEmpty } from 'lodash';
import { useDebounceSearch } from '@zonofi/seller-hub/hooks';

interface SelectPriceListDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPricelist: (pricelistId: string, pricelistName: string) => void;
}

export const SelectPriceListDrawer: React.FC<SelectPriceListDrawerProps> = ({
  isOpen,
  onClose,
  onSelectPricelist,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [priceList, setPriceList] = useState<Pricing[]>([]);
  const [currentPricelist, setCurrentPricelist] = useState<OrderPricelist>({
    pricelistId: '',
    pricelistName: '',
  });

  const { debouncedSearchValue } = useDebounceSearch();

  const selectedPricelist = useAppSelector(getOrderPricelist);
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);

  const [getPriceListApi, priceListApiResponse] =
    useGetWorkspacePricelistMutation();

  useEffect(() => {
    if (!isOpen) return;

    if (selectedPricelist?.pricelistId) {
      setCurrentPricelist({
        pricelistId: selectedPricelist.pricelistId,
        pricelistName: selectedPricelist.pricelistName,
      });
    } else {
      setCurrentPricelist({
        pricelistId: '',
        pricelistName: '',
      });
    }
  }, [isOpen, selectedPricelist]);

  useEffect(() => {
    getPriceListApi({
      priceListRequest: {
        sellerWorkspaceId,
        pageNo: 1,
        pageSize: 50,
        workspaceId: sellerWorkspaceId,
        status: 'Active',
        criteria: [],
        ...(debouncedSearchValue ? { searchKey: debouncedSearchValue } : {}),
      },
    });
  }, [debouncedSearchValue, sellerWorkspaceId]);

  useEffect(() => {
    if (priceListApiResponse.isSuccess && priceListApiResponse.data) {
      setPriceList(priceListApiResponse.data?.pricing ?? []);
      setIsLoading(false);
    }
  }, [priceListApiResponse]);

  const handleClose = () => {
    setCurrentPricelist({
      pricelistId: selectedPricelist?.pricelistId ?? '',
      pricelistName: selectedPricelist?.pricelistName ?? '',
    });
    onClose?.();
  };

  return (
    <BottomModal
      leftText="Close"
      isVisible={isOpen}
      onLeftAction={handleClose}
      title="Select Pricelist"
      showSearch
      rightText="Clear"
      onRightAction={() =>
        setCurrentPricelist({
          pricelistId: '',
          pricelistName: '',
        })
      }
      modalContainerStyles={{ height: '85vh' }}
      searchPlaceholder="Search Pricelist"
      bottomButton="Apply"
      isDisabled={isEmpty(priceList) || isEmpty(currentPricelist?.pricelistId)}
      onBottomAction={() => {
        if (currentPricelist?.pricelistId && currentPricelist?.pricelistName) {
          onSelectPricelist(
            currentPricelist.pricelistId,
            currentPricelist.pricelistName
          );
        }
        dispatch(removeSearchKey());
        onClose?.();
      }}
      leftTextStyle="close-text"
      rightTextStyle="clear-text"
    >
      <SelectPriceListDrawerContent
        selectedPricelist={currentPricelist}
        setSelectedPricelist={setCurrentPricelist}
        priceList={priceList}
        isloading={isLoading}
      />
    </BottomModal>
  );
};
